// Compatibility shim: GET /v1/dashboard/transaction/:id still serves the bare
// `transactions` row on environments without BS-218. Delete once the DTO is
// deployed everywhere.

const CRYPTO = 'CRYPTO';

const findCurrency = (currencies = [], currencyId) => {
  if (currencyId === null || currencyId === undefined) return null;
  return currencies.find(({ id, value }) => String(id ?? value) === String(currencyId)) ?? null;
};

const parseMerchantMeta = (meta) => {
  try {
    return JSON.parse(meta?.meta || '{}');
  } catch {
    return {};
  }
};

const getMerchantReference = (meta) => {
  const merchantMeta = parseMerchantMeta(meta);
  return (
    merchantMeta.merchant_reference ||
    merchantMeta.reference ||
    merchantMeta.order_reference ||
    merchantMeta.order_id ||
    null
  );
};

const classifyPayment = (expected, paid) => {
  if (expected === null || expected === undefined || paid === null || paid === undefined) {
    return 'NOT_AVAILABLE';
  }
  const difference = Number(paid) - Number(expected);
  if (difference === 0) return 'EXACT';
  return difference < 0 ? 'UNDERPAID' : 'OVERPAID';
};

const money = (amount, currency) => ({
  amount: amount ?? null,
  currency: currency?.slug ?? null
});

const isLegacyTransaction = (payload) =>
  Boolean(payload) && !payload.summary && payload.reference !== undefined;

const adaptLegacyTransaction = (transaction, currencies) => {
  const paymentCurrency = findCurrency(currencies, transaction.payment_currency);
  const initializedCurrency = findCurrency(currencies, transaction.initialized_currency);
  const settlementCurrency = findCurrency(currencies, transaction.settlement_currency);

  const merchantReference = getMerchantReference(transaction.meta);
  const expected = transaction.payment_amount;
  const paid = transaction.amount_paid;
  const difference =
    expected === null || expected === undefined || paid === null || paid === undefined
      ? null
      : Number(paid) - Number(expected);

  const summary = {
    status: transaction.status,
    type: transaction.type,
    description: transaction.description,
    basqet_transaction_id: transaction.id,
    basqet_reference: transaction.reference,
    merchant_reference: merchantReference,
    merchant_reference_label: 'Merchant reference',
    created_at: transaction.created_at,
    completed_at: transaction.completed_at ?? null
  };

  return {
    header: {
      status: summary.status,
      reference: summary.basqet_reference,
      merchant_reference: merchantReference,
      merchant_reference_label: summary.merchant_reference_label,
      created_at: summary.created_at
    },
    summary,
    amounts: {
      initialized: money(transaction.initialized_amount, initializedCurrency),
      expected_payment: money(expected, paymentCurrency),
      amount_paid: money(paid, paymentCurrency),
      payment_difference: money(difference, paymentCurrency),
      payment_classification: classifyPayment(expected, paid),
      basqet_fee: money(transaction.fee, paymentCurrency),
      settlement_amount: money(transaction.settlement_amount, settlementCurrency)
    },
    // The legacy response carries no customer, virtual account, webhook or log
    // relations, so those sections render their empty states.
    customer: { name: null, email: null },
    payment:
      paymentCurrency?.type === CRYPTO && transaction.payment_address
        ? {
            method: CRYPTO,
            crypto: {
              wallet_address: transaction.payment_address,
              txid: transaction.txid ?? null,
              expiry_date: transaction.expiry_date ?? null
            }
          }
        : { method: 'NOT_AVAILABLE' },
    fees: {
      basqet_fee: money(transaction.fee, paymentCurrency),
      payment_fee_bearer: transaction.meta?.fee_bearer ?? null,
      settlement_fee_bearer: transaction.meta?.settlement_fee_bearer ?? null
    },
    webhook_delivery: {
      latest: {
        event: null,
        status: 'NOT_AVAILABLE',
        attempted_at: null,
        attempts: 0,
        http_response_code: null
      },
      attempts: []
    },
    timeline: []
  };
};

export { adaptLegacyTransaction, isLegacyTransaction };
